import { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Sidebar, SidebarNav } from './Sidebar'
import { Header } from './Header'
import { CommandPalette } from './CommandPalette'
import { ShortcutsHelp } from './ShortcutsHelp'
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts'
import { OfflineBanner } from '@/components/OfflineBanner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { PageContextProvider, usePageEntity } from '../../contexts/PageContext'
import { ChatLauncherProvider } from '../../contexts/ChatLauncherContext'
import { ChatFab } from '../chat/ChatFab'
import { AiSidebar } from '../chat/AiSidebar'
import { QuickCaptureDialog } from '../notes/QuickCaptureDialog'
import { useMediaQuery } from '@/hooks/use-media-query'

// Wide screens dock the assistant alongside page content instead of overlaying it.
const DOCK_QUERY = '(min-width: 1280px)'

export function AppLayout() {
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  // On wide screens the assistant is docked, so open it by default.
  const isWide = useMediaQuery(DOCK_QUERY)
  const [chatOpen, setChatOpen] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DOCK_QUERY).matches,
  )
  const [chatPrompt, setChatPrompt] = useState<string | null>(null)
  const [captureOpen, setCaptureOpen] = useState(false)

  // Open the AI sidebar prefilled with a prompt (the Today ask bar).
  const openChatWith = (prompt: string) => { setChatPrompt(prompt); setChatOpen(true) }
  const [helpOpen, setHelpOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  // Track P bare-key layer: / search · n new · ? help · g+key nav.
  useKeyboardShortcuts({
    onSearch: () => setPaletteOpen(true),
    onNew: () => setCaptureOpen(true),
    onHelp: () => setHelpOpen((o) => !o),
    onNavigate: (path) => navigate(path),
  })

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setPaletteOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setChatOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  // ⌘N / Ctrl+N — quick-capture a note against whatever record is on screen.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'n' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setCaptureOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  return (
    <PageContextProvider>
     <ChatLauncherProvider value={openChatWith}>
      <TooltipProvider>
        <div className="flex h-screen overflow-hidden">
          <OfflineBanner />

          {/* Desktop rail */}
          <Sidebar onOpenChat={() => setChatOpen(true)} />

          {/* Mobile drawer — same nav, off-canvas from the left below md */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="left"
              className="w-72 max-w-[82vw] p-0 md:hidden [&>button]:text-muted-foreground"
            >
              <SheetTitle className="sr-only">Main navigation</SheetTitle>
              <nav aria-label="Mobile navigation" className="flex h-full flex-col">
                <SidebarNav
                  onNavigate={() => setMobileNavOpen(false)}
                  onOpenChat={() => setChatOpen(true)}
                />
              </nav>
            </SheetContent>
          </Sheet>

          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <Header
              onSearchClick={() => setPaletteOpen(true)}
              onMenuClick={() => setMobileNavOpen(true)}
            />
            <main className="flex-1 overflow-y-auto p-4 md:p-6">
              <Outlet />
            </main>
          </div>
          {/* Docked assistant (wide screens) sits in-flow alongside content; on narrow
              screens AiSidebar renders itself as an overlay Sheet via a portal. */}
          <AiSidebar
            open={chatOpen}
            onOpenChange={setChatOpen}
            initialPrompt={chatPrompt}
            onPromptConsumed={() => setChatPrompt(null)}
            docked={isWide}
          />
          <CommandPalette
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            onOpenChat={() => setChatOpen(true)}
          />
          {/* FAB is redundant while the assistant is docked and open. */}
          {!(isWide && chatOpen) && <ChatFab onClick={() => setChatOpen(true)} />}
          <QuickCapture open={captureOpen} onOpenChange={setCaptureOpen} />
          <ShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} />
        </div>
      </TooltipProvider>
     </ChatLauncherProvider>
    </PageContextProvider>
  )
}

// Reads the on-screen entity from PageContext (so it must live inside the provider)
// and feeds it to the ⌘N quick-capture dialog.
function QuickCapture({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const entity = usePageEntity()
  return <QuickCaptureDialog open={open} entity={entity} onOpenChange={onOpenChange} />
}
